// src/pages/CheckoutPage.jsx
import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingBag, ChevronDown, ChevronRight, Check, Lock, ArrowDown, AlertCircle } from "lucide-react";
import { useCart } from "../Context/Cartcontext";
import { apiFetch } from "../api/api";

function FontImport() {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;1,9..144,500&family=DM+Sans:wght@400;500;600;700&display=swap');
      .og-display{font-family:'Fraunces',serif}
      .og-body{font-family:'DM Sans',sans-serif}
    `}</style>
  );
}

// Simple theme — white base, one yellow accent, gray borders/text like a normal checkout.
const YELLOW = "#FFD400";

const INDIAN_STATES = [
  "Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh",
  "Goa","Gujarat","Haryana","Himachal Pradesh","Jharkhand","Karnataka",
  "Kerala","Madhya Pradesh","Maharashtra","Manipur","Meghalaya","Mizoram",
  "Nagaland","Odisha","Punjab","Rajasthan","Sikkim","Tamil Nadu","Telangana",
  "Tripura","Uttar Pradesh","Uttarakhand","West Bengal",
  "Andaman and Nicobar Islands","Chandigarh","Delhi","Jammu and Kashmir",
  "Ladakh","Lakshadweep","Puducherry",
];

// ── Validation helpers ───────────────────────────────────────────────────────
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[6-9]\d{9}$/;      // 10-digit Indian mobile
const PIN_RE   = /^\d{6}$/;           // 6-digit PIN code
const NAME_RE  = /^[A-Za-z\s.'-]{2,}$/;

function validateForm(form) {
  const errors = {};

  if (!form.email.trim()) errors.email = "Email is required";
  else if (!EMAIL_RE.test(form.email.trim())) errors.email = "Enter a valid email address";

  if (!form.firstName.trim()) errors.firstName = "First name is required";
  else if (!NAME_RE.test(form.firstName.trim())) errors.firstName = "Enter a valid name";

  if (!form.lastName.trim()) errors.lastName = "Last name is required";
  else if (!NAME_RE.test(form.lastName.trim())) errors.lastName = "Enter a valid name";

  if (!form.address.trim()) errors.address = "Address is required";
  else if (form.address.trim().length < 5) errors.address = "Address looks too short";

  if (!form.city.trim()) errors.city = "City is required";
  else if (!NAME_RE.test(form.city.trim())) errors.city = "Enter a valid city name";

  if (!form.state.trim()) errors.state = "Please select a state";

  if (!form.pin.trim()) errors.pin = "PIN code is required";
  else if (!PIN_RE.test(form.pin.trim())) errors.pin = "Enter a valid 6-digit PIN code";

  if (!form.phone.trim()) errors.phone = "Phone number is required";
  else if (!PHONE_RE.test(form.phone.trim())) errors.phone = "Enter a valid 10-digit mobile number";

  return errors;
}

function Input({ placeholder, value, onChange, onBlur, type = "text", error, inputMode, maxLength, name }) {
  return (
    <div className="w-full">
      <input
        type={type}
        name={name}
        inputMode={inputMode}
        maxLength={maxLength}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        aria-invalid={!!error}
        className={`w-full rounded-lg border px-4 py-3 text-sm text-[#1A1A1A] placeholder-gray-400 focus:outline-none bg-white transition-colors ${
          error
            ? "border-red-400 focus:border-red-500"
            : "border-gray-300 focus:border-[#1A1A1A]"
        }`}
      />
      {error && (
        <p className="flex items-center gap-1 text-[11px] text-red-500 mt-1.5">
          <AlertCircle size={12} className="flex-shrink-0" />
          {error}
        </p>
      )}
    </div>
  );
}

function StateDropdown({ value, onChange, error, onOpenChange }) {
  const [open, setOpen] = useState(false);
  const toggle = (v) => {
    setOpen(v);
    onOpenChange?.();
  };
  return (
    <div className="relative w-full">
      <button type="button" onClick={() => toggle(!open)}
        className={`w-full flex items-center justify-between rounded-lg border bg-white px-4 py-3 text-sm text-left focus:outline-none transition-colors ${
          error ? "border-red-400" : "border-gray-300 focus:border-[#1A1A1A]"
        }`}>
        <span className={value ? "text-[#1A1A1A]" : "text-gray-400"}>{value || "State"}</span>
        <ChevronDown size={15} className={`text-gray-400 flex-shrink-0 transition-transform ${open?"rotate-180":""}`}/>
      </button>
      {error && (
        <p className="flex items-center gap-1 text-[11px] text-red-500 mt-1.5">
          <AlertCircle size={12} className="flex-shrink-0" />
          {error}
        </p>
      )}
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => toggle(false)}/>
          <div
            className="absolute left-0 right-0 top-full mt-1 z-20 bg-white border border-gray-200 rounded-lg shadow-xl max-h-52 overflow-y-auto"
            data-lenis-prevent
          >
            {INDIAN_STATES.map(s => (
              <button key={s} type="button"
                onClick={() => { onChange(s); toggle(false); }}
                className={`w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 flex items-center justify-between ${value===s?"font-semibold bg-gray-50":""}`}>
                {s}{value===s && <Check size={13} className="text-[#1A1A1A]"/>}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

const PAYMENT_METHODS = [
  { id:"razorpay", label:"Razorpay (UPI, Cards, Net Banking, Wallets)", icons:["UPI","VISA","MC","NB"] },
  { id:"cod",      label:"Cash on Delivery", icons:["COD"] },
];

const ICON_COLORS = {
  VISA:"#1A1F71", MC:"#EB001B", AMEX:"#007BC1", RuPay:"#097939",
  UPI:"#097939", NB:"#1A1A1A", COD:"#1A1A1A",
};

function IconPill({ label }) {
  return (
    <div className="h-6 px-2 rounded border border-gray-200 bg-white flex items-center">
      <span className="text-[9px] font-bold" style={{ color: ICON_COLORS[label]||"#888" }}>{label}</span>
    </div>
  );
}

// `bordered` prop kept for API compatibility with the mobile drawer call site.
// Items now carry numbered black badges (position in cart, like the Shopify-style
// reference) and a floating "Scroll for more items" pill appears whenever the
// list overflows its box — it fades out once the user scrolls near the bottom.
//
// Coupon state now lives in the parent (CheckoutPage) and is passed down as props,
// because the final order submission also needs to know the applied discount.
function OrderSummary({
  cartItems, bordered = true,
  coupon = null,
  couponInput = "",
  setCouponInput = () => {},
  couponError = "",
  applyingCoupon = false,
  onApply = () => {},
  onRemove = () => {},
}) {
  const [showScrollHint, setShowScrollHint] = useState(cartItems.length > 3);
  const listRef = useRef(null);
  const subtotal = cartItems.reduce((s,i) => s+i.price*(i.qty||1), 0);
  const discount = coupon?.discount || 0;
  const total = Math.max(0, subtotal - discount);

  const handleScroll = () => {
    const el = listRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 20;
    setShowScrollHint(!nearBottom && el.scrollHeight > el.clientHeight);
  };

  return (
    <div className={`bg-[#F9F9F9] h-full  px-5 sm:px-8 py-6 sm:py-8 flex flex-col gap-5 ${bordered ? "border-l border-gray-200" : ""}`}>
      {/* Items scrollable, with numbered badges + scroll hint */}
      <div className="relative">
      <div
  ref={listRef}
  onScroll={handleScroll}
  className="flex flex-col gap-4 overflow-y-auto"
  style={{ maxHeight:300 }}
  data-lenis-prevent
>
          {cartItems.map((item,i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="relative flex-shrink-0">
                <div className="w-14 h-14 rounded-lg overflow-hidden bg-white border border-gray-200">
                  <img src={item.image} alt={item.name} className="w-full h-full object-cover"/>
                </div>
                <span className="absolute -top-1.5 -left-1.5 w-5 h-5 rounded-full bg-[#1A1A1A] text-white text-[10px] font-bold flex items-center justify-center">
                  {i+1}
                </span>
              </div>
          <div className="flex-1 min-w-0">
  <p className="text-sm font-medium leading-snug text-[#1A1A1A]">{item.name}</p>
  {item.personalisedName && (
    <p className="text-xs font-semibold text-gray-600 mt-1">
      Text: <span className="font-normal">{item.personalisedName}</span>
    </p>
  )}
  {item.paperType && <p className="text-xs text-gray-500 mt-0.5">{item.paperType}</p>}
  {item.color && <p className="text-xs text-gray-500 mt-0.5">{item.color}</p>}
  {item.qty > 1 && <p className="text-xs text-gray-500 mt-0.5">Qty: {item.qty}</p>}
</div>
              <span className="text-sm font-medium flex-shrink-0">₹{(item.price*(item.qty||1)).toLocaleString("en-IN")}.00</span>
            </div>
          ))}
        </div>
        {showScrollHint && (
          <button
            onClick={() => listRef.current?.scrollBy({ top: 120, behavior: "smooth" })}
            className="absolute bottom-1 left-1/2 -translate-x-1/2 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#1A1A1A]/90 text-white text-xs font-medium shadow-md hover:bg-[#1A1A1A] transition-colors"
          >
            Scroll for more items <ArrowDown size={12}/>
          </button>
        )}
      </div>

      {/* Discount */}
      <div>
        {!coupon ? (
          <div className="flex gap-2">
            <input
              value={couponInput}
              onChange={e=>setCouponInput(e.target.value)}
              placeholder="Discount code or gift card"
              className="flex-1 min-w-0 rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm focus:border-[#1A1A1A] focus:outline-none"
            />
            <button
              onClick={onApply}
              disabled={applyingCoupon || !couponInput.trim()}
              className="px-4 py-2.5 rounded-lg border border-gray-300 bg-white text-sm font-medium hover:bg-gray-50 flex-shrink-0 disabled:opacity-50"
            >
              {applyingCoupon ? "..." : "Apply"}
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between rounded-lg border border-green-300 bg-green-50 px-3 py-2.5">
            <span className="text-sm font-semibold text-green-700">✓ {coupon.code} applied</span>
            <button onClick={onRemove} className="text-xs text-gray-500 underline">Remove</button>
          </div>
        )}
        {couponError && <p className="text-[11px] text-red-500 mt-1.5">{couponError}</p>}
      </div>

      {/* Totals */}
      <div className="border-t border-gray-300 pt-4 flex flex-col gap-2">
        <div className="flex justify-between text-sm text-gray-600">
          <span>Subtotal · {cartItems.reduce((s,i)=>s+(i.qty||1),0)} items</span>
          <span>₹{subtotal.toLocaleString("en-IN")}.00</span>
        </div>

        {discount > 0 && (
          <div className="flex justify-between text-sm text-green-600 font-semibold">
            <span>Discount ({coupon.code})</span>
            <span>−₹{discount.toLocaleString("en-IN")}.00</span>
          </div>
        )}

        <div className="flex justify-between text-sm text-gray-600">
          <span>Shipping</span>
          <span className="text-green-600 font-semibold">FREE</span>
        </div>
      </div>

      <div className="border-t border-gray-300 pt-4 flex justify-between items-center">
        <span className="font-bold text-base">Total</span>
        <div>
          <span className="text-xs text-gray-500 mr-1">INR</span>
          <span className="font-bold text-xl">₹{total.toLocaleString("en-IN")}.00</span>
        </div>
      </div>
      {/* <p className="text-xs text-gray-400">Including ₹{Math.round(subtotal*0.15).toLocaleString("en-IN")} in taxes</p> */}
    </div>
  );
}


export default function CheckoutPage() {
  const { cartItems } = useCart();
  const navigate = useNavigate();

  // 👇 NAYA: Razorpay script sirf checkout page pe load hogi, poore app me nahi
  useEffect(() => {
    if (!window.Razorpay) {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);

  const [form, setForm] = useState({
    email:"", newsletter:true,
    firstName:"", lastName:"",
    address:"", apt:"",
    city:"", state:"", pin:"", phone:"",
    saveInfo:false, textUpdates:false,
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [paymentMethod, setPaymentMethod] = useState("razorpay");
  const [billingAddress, setBillingAddress] = useState("same");
  const [showMobileSummary, setShowMobileSummary] = useState(false);
  const [placing, setPlacing] = useState(false); // true jab order/payment process ho raha ho

  const deliveryRef = useRef(null);
  const fieldRefs = useRef({});

  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const subtotal = cartItems.reduce((s,i)=>s+i.price*(i.qty||1),0);

  // ── Coupon state ─────────────────────────────────────────────────────────
  const [coupon, setCoupon] = useState(null);   // { code, discount, freeShipping }
  const [couponInput, setCouponInput] = useState("");
  const [couponError, setCouponError] = useState("");
  const [applyingCoupon, setApplyingCoupon] = useState(false);

  const discountAmount = coupon?.discount || 0;
  const finalTotal = Math.max(0, subtotal - discountAmount);

  const handleApplyCoupon = async () => {
    const code = couponInput.trim().toUpperCase();
    if (!code) return;
    setCouponError("");
    setApplyingCoupon(true);
    try {
      const res = await apiFetch("/coupons/validate.php", {
        method: "POST",
        body: { code, subtotal },
      });
      setCoupon(res.data);   // yahi se automatically neeche lines dikhengi
    } catch (err) {
      setCoupon(null);
      setCouponError(err.message || "Invalid coupon code.");
    } finally {
      setApplyingCoupon(false);
    }
  };

  const handleRemoveCoupon = () => {
    setCoupon(null);
    setCouponInput("");
    setCouponError("");
  };

  // Field ke blur hone par uska error nikal ke turant dikhado (better UX)
  const markTouched = (k) => {
    setTouched(t => ({ ...t, [k]: true }));
    const allErrors = validateForm(form);
    setErrors(e => ({ ...e, [k]: allErrors[k] }));
  };

  // Live-update: agar field pehle se touched/error state mein hai, to typing ke saath hi error clear/update karo
  const onFieldChange = (k, v) => {
    set(k, v);
    if (touched[k]) {
      const nextForm = { ...form, [k]: v };
      const allErrors = validateForm(nextForm);
      setErrors(e => ({ ...e, [k]: allErrors[k] }));
    }
  };

  const registerRef = (k) => (el) => { fieldRefs.current[k] = el; };

  const scrollToFirstError = (errs) => {
    const order = ["email","firstName","lastName","address","city","state","pin","phone"];
    const firstKey = order.find(k => errs[k]);
    if (!firstKey) return;
    const el = fieldRefs.current[firstKey];
    if (el?.scrollIntoView) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    } else {
      deliveryRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  // Cart items ko backend ke expected format mein convert karo
  const buildItems = () =>
    cartItems.map(i => ({
      productId: i.id ?? null,
      productName: i.name,
      quantity: i.qty || 1,
      price: i.price,
      variantInfo: [i.paperType, i.color].filter(Boolean).join(" / ") || null,
      personalisedName: i.personalisedName || null,   // ← ye line add karein agar nahi ki
    }));

  // Form ke customer/delivery fields ko backend ke expected format mein convert karo.
  // Coupon code + discount bhi bhej rahe hain — lekin backend (create_razorpay_order.php /
  // place_cod.php) ko yeh discount client se bharosa karke NAHI leni chahiye; wahan par
  // coupon code se dobara subtotal ke against discount calculate karo (server-side).
  const buildCustomer = () => ({
    customer: `${form.firstName} ${form.lastName}`.trim(),
    email: form.email.trim(),
    phone: form.phone.trim(),
    city: form.city.trim(),
    state: form.state,
    pincode: form.pin.trim(),
    address: `${form.address.trim()}${form.apt ? ", " + form.apt.trim() : ""}`,
    items: buildItems(),
    couponCode: coupon?.code || null,
    discountAmount: discountAmount,
    totalAmount: finalTotal,
  });


  // Cart mein jitne bhi photobook items hain (jinke paas orderRef hai), unke
// DB record mein customer naam/email attach karo. Ye best-effort hai —
// isme koi error aaye to bhi checkout rukna nahi chahiye.
const updatePhotobookCustomerInfo = async () => {
  console.log("🔍 CART ITEMS:", JSON.stringify(cartItems, null, 2));
  const photobookItems = cartItems.filter(i => i.orderRef);
  console.log("🔍 PHOTOBOOK ITEMS (with orderRef):", photobookItems);

  if (photobookItems.length === 0) {
    console.warn("🔍 SKIP: koi bhi cart item mein orderRef nahi mila!");
    return;
  }

  const customerName = `${form.firstName} ${form.lastName}`.trim();
  const customerEmail = form.email.trim();
  console.log("🔍 Sending customerName:", customerName, "customerEmail:", customerEmail);

  const results = await Promise.allSettled(
    photobookItems.map(item =>
      apiFetch("/photobook/orders-update-customer.php", {
        method: "PUT",
        body: {
          orderRef: item.orderRef,
          customerName,
          customerEmail,
        },
      })
    )
  );
  console.log("🔍 API RESULTS:", results);
};

  const handlePayNow = async () => {
    const validationErrors = validateForm(form);
    setErrors(validationErrors);
    setTouched({
      email:true, firstName:true, lastName:true, address:true,
      city:true, state:true, pin:true, phone:true,
    });

    if (Object.keys(validationErrors).length > 0) {
      scrollToFirstError(validationErrors);
      return;
    }

setPlacing(true);
    try {
      // Photobook orders (agar cart mein hain) ko customer info se link karo —
      // fire-and-forget, checkout ka flow isse block nahi hona chahiye
     await updatePhotobookCustomerInfo();

      if (paymentMethod === "razorpay") {
        // 1) Backend par order turant DB mein save ho jaata hai (status: Pending)
        //    + Razorpay ka order bhi ban jaata hai checkout popup ke liye.
        //    Isse customer ki detail admin panel mein turant dikhne lag jaati hai,
        //    payment successful ho ya na ho.
        const rzpOrder = await apiFetch("/orders/create_razorpay_order.php", {
          method: "POST",
          body: buildCustomer(), // customer + address + items + coupon sab ek saath
        });

        const { key, amount, currency, razorpayOrderId, dbOrderId, orderNumber, testMode } = rzpOrder.data;

        const options = {
          key,
          amount,
          currency,
          ...(razorpayOrderId ? { order_id: razorpayOrderId } : {}), // test mode mein order_id nahi bhejte
          name: "Aishcraft",
          description: "Order payment",
          prefill: {
            name: `${form.firstName} ${form.lastName}`,
            email: form.email,
            contact: form.phone,
          },
          handler: async function (response) {
            // Test mode mein (bina razorpayOrderId ke) signature verify nahi ho sakti —
            // order already DB mein "Pending" save hai, bas seedha success page pe le jao.
            if (!testMode) {
              try {
                await apiFetch("/orders/verify_payment.php", {
                  method: "POST",
                  body: {
                    dbOrderId,
                    razorpay_order_id: response.razorpay_order_id,
                    razorpay_payment_id: response.razorpay_payment_id,
                    razorpay_signature: response.razorpay_signature,
                  },
                });
              } catch (err) {
                console.error("Verify failed:", err.message);
              }
            }
            setPlacing(false);
            navigate("/order-successs", { state: { orderId: orderNumber } });
          },
          modal: {
            ondismiss: function () {
              // User ne popup band kar diya bina pay kiye — order already "Pending" save ho chuka hai.
              setPlacing(false);
              alert("Order save ho gaya hai (Pending). Aap baad mein payment complete kar sakte hain.");
            },
          },
          theme: { color: "#1A1A1A" },
        };
        const razorpay = new window.Razorpay(options);
        razorpay.open();
        return; // setPlacing(false) yahan nahi karna — handler/ondismiss mein hoga
      }

      // COD
      const result = await apiFetch("/orders/place_cod.php", {
        method: "POST",
        body: buildCustomer(),
      });
      navigate("/order-successs", { state: { orderId: result.data.orderNumber } });

    } catch (err) {
      alert(err.message || "Order place nahi ho paya. Dobara try karein.");
    } finally {
      if (paymentMethod !== "razorpay") setPlacing(false);
    }
  };

  if (cartItems.length === 0) {
    return (
      <div className="og-body grid min-h-screen place-items-center bg-white text-[#1A1A1A] px-4">
        <FontImport/>
        <div className="text-center">
          <ShoppingBag size={48} className="mx-auto text-gray-200 mb-4"/>
          <h1 className="og-display text-2xl italic mb-2">Your bag is empty</h1>
          <p className="text-gray-500 text-sm mb-6">Add some products before checking out.</p>
          <button onClick={() => navigate("/")} className="rounded-full bg-[#1A1A1A] px-6 py-2.5 text-sm font-semibold text-white hover:opacity-90">
            Continue Shopping
          </button>
        </div>
      </div>
    );
  }

  const payBtnLabel = () => {
    if (placing) return paymentMethod === "razorpay" ? "Processing..." : "Placing Order...";
    if (paymentMethod==="razorpay") return "Continue to Razorpay";
    return "Place Order (COD)";
  };

  return (
    <div className="og-body min-h-screen bg-white text-[#1A1A1A]">
      <FontImport/>

      {/* Header */}
      <header className="border-b border-gray-200 px-4 sm:px-6 py-3.5 sm:py-4">
        <div className="mx-auto max-w-6xl flex items-center justify-between">
          <Link to="/" className="og-display text-xl sm:text-2xl italic font-semibold">Aishcraft</Link>
          <div className="flex items-center gap-1.5 text-xs text-gray-400">
            <Lock size={13}/>
            <span className="hidden xs:inline">Secure checkout</span>
          </div>
        </div>
      </header>

      {/* MOBILE ORDER SUMMARY */}
      <div className="lg:hidden border-b border-gray-200 px-4 py-3">
        <button
          onClick={() => setShowMobileSummary(s => !s)}
          className="w-full flex items-center justify-between"
        >
          <span className="flex items-center gap-1.5 text-sm font-medium text-[#1A1A1A]">
            <ShoppingBag size={15}/>
            {showMobileSummary ? "Hide" : "Show"}  Cupan Code 
            <ChevronDown size={14} className={`text-gray-400 transition-transform ${showMobileSummary ? "rotate-180" : ""}`}/>
          </span>
          <span className="font-bold text-sm">₹{finalTotal.toLocaleString("en-IN")}.00</span>
        </button>
        {showMobileSummary && (
          <div className="mt-3 -mx-4">
            <OrderSummary
              cartItems={cartItems} bordered={false}
              coupon={coupon} couponInput={couponInput} setCouponInput={setCouponInput}
              couponError={couponError} applyingCoupon={applyingCoupon}
              onApply={handleApplyCoupon} onRemove={handleRemoveCoupon}
            />
          </div>
        )}
      </div>

      <div className="mx-auto max-w-6xl grid lg:grid-cols-[1fr_400px] min-h-[calc(100vh-65px)]">

        {/* LEFT */}
        <div className="px-4 sm:px-6 py-6 sm:py-10 lg:pr-12 pb-24 lg:pb-10">

          {/* Breadcrumb */}
          <div className="flex items-center flex-wrap gap-x-2 gap-y-1 text-xs text-gray-700 mb-6 sm:mb-8">
            <Link to="/cart" className="hover:text-[#1A1A1A]">Cart</Link>
            <ChevronRight size={12}/>
            <span className="font-semibold text-[#1A1A1A]">Information</span>
            <ChevronRight size={12}/><span>Shipping</span>
            <ChevronRight size={12}/><span>Payment</span>
          </div>

          {/* Contact */}
          <section className="mb-7 sm:mb-8">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-base font-bold">Contact</h2>
              
              </div>
            <div ref={registerRef("email")}>
              <Input
                placeholder="Email"
                type="email"
                value={form.email}
                onChange={e=>onFieldChange("email", e.target.value)}
                onBlur={()=>markTouched("email")}
                error={touched.email ? errors.email : ""}
              />
            </div>
            <label className="flex items-center gap-2 mt-3 cursor-pointer">
              <input type="checkbox" checked={form.newsletter} onChange={e=>set("newsletter",e.target.checked)} className="w-4 h-4 accent-[#1A1A1A] flex-shrink-0"/>
              <span className="text-xs text-gray-600">Email me with news and offers</span>
            </label>
          </section>

          {/* Delivery */}
          <section className="mb-7 sm:mb-8" ref={deliveryRef}>
            <h2 className="text-base font-bold mb-4">Delivery</h2>
            <div className="flex flex-col gap-3">
              <div className="rounded-lg border border-gray-300 px-4 py-3 text-sm text-gray-500 bg-gray-50">India</div>

              <div className="grid grid-cols-1 xs:grid-cols-2 gap-3">
                <div ref={registerRef("firstName")}>
                  <Input placeholder="First name" value={form.firstName}
                    onChange={e=>onFieldChange("firstName", e.target.value)}
                    onBlur={()=>markTouched("firstName")}
                    error={touched.firstName ? errors.firstName : ""}/>
                </div>
                <div ref={registerRef("lastName")}>
                  <Input placeholder="Last name" value={form.lastName}
                    onChange={e=>onFieldChange("lastName", e.target.value)}
                    onBlur={()=>markTouched("lastName")}
                    error={touched.lastName ? errors.lastName : ""}/>
                </div>
              </div>

              <div className="relative" ref={registerRef("address")}>
                <Input placeholder="Address" value={form.address}
                  onChange={e=>onFieldChange("address", e.target.value)}
                  onBlur={()=>markTouched("address")}
                  error={touched.address ? errors.address : ""}/>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="absolute right-3 top-3 w-4 h-4 text-gray-400 pointer-events-none">
                  <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
                </svg>
              </div>

              <Input placeholder="Apartment, suite, etc. (optional)" value={form.apt} onChange={e=>set("apt",e.target.value)}/>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div ref={registerRef("city")}>
                  <Input placeholder="City" value={form.city}
                    onChange={e=>onFieldChange("city", e.target.value)}
                    onBlur={()=>markTouched("city")}
                    error={touched.city ? errors.city : ""}/>
                </div>
                <div ref={registerRef("state")}>
                  <StateDropdown
                    value={form.state}
                    onChange={v=>{ onFieldChange("state", v); setTouched(t=>({...t, state:true})); }}
                    error={touched.state ? errors.state : ""}
                  />
                </div>
                <div className="col-span-2 sm:col-span-1" ref={registerRef("pin")}>
                  <Input placeholder="PIN code" value={form.pin} inputMode="numeric" maxLength={6}
                    onChange={e=>onFieldChange("pin", e.target.value.replace(/\D/g,""))}
                    onBlur={()=>markTouched("pin")}
                    error={touched.pin ? errors.pin : ""}/>
                </div>
              </div>

              <div ref={registerRef("phone")}>
                <Input placeholder="Phone" type="tel" inputMode="numeric" maxLength={10} value={form.phone}
                  onChange={e=>onFieldChange("phone", e.target.value.replace(/\D/g,""))}
                  onBlur={()=>markTouched("phone")}
                  error={touched.phone ? errors.phone : ""}/>
              </div>

              <div className="flex flex-col gap-2 mt-1">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.saveInfo} onChange={e=>set("saveInfo",e.target.checked)} className="w-4 h-4 accent-[#1A1A1A] flex-shrink-0"/>
                  <span className="text-xs text-gray-600">Save this information for next time</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.textUpdates} onChange={e=>set("textUpdates",e.target.checked)} className="w-4 h-4 accent-[#1A1A1A] flex-shrink-0"/>
                  <span className="text-xs text-gray-600">Text me with news and offers</span>
                </label>
              </div>
            </div>
          </section>

          {/* Shipping method */}
          <section className="mb-7 sm:mb-8">
            <h2 className="text-base font-bold mb-4">Shipping method</h2>
            <div className="rounded-lg border-2 border-[#1A1A1A] px-4 py-3.5 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-4 h-4 rounded-full border-2 border-[#1A1A1A] flex items-center justify-center flex-shrink-0">
                  <div className="w-2 h-2 rounded-full bg-[#1A1A1A]"/>
                </div>
                <span className="text-sm font-semibold truncate">Express Delivery</span>
              </div>
              <span className="text-sm font-bold text-green-600 flex-shrink-0">FREE</span>
            </div>
          </section>

          {/* Payment method */}
          <section className="mb-7 sm:mb-8">
            <h2 className="text-base font-bold mb-4">Payment</h2>
            <p className="text-xs text-gray-400 mb-3">All transactions are secure and encrypted.</p>
            <div className="rounded-lg border border-gray-200 overflow-hidden divide-y divide-gray-200">
              {PAYMENT_METHODS.map(pm => (
                <label key={pm.id} onClick={() => setPaymentMethod(pm.id)}
                  className="flex items-center gap-3 px-4 py-3.5 cursor-pointer hover:bg-gray-50">
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${paymentMethod===pm.id?"border-[#1A1A1A]":"border-gray-300"}`}>
                    {paymentMethod===pm.id && <div className="w-2 h-2 rounded-full bg-[#1A1A1A]"/>}
                  </div>
                  <span className="text-sm font-medium flex-1 min-w-0">{pm.label}</span>
                </label>
              ))}
            </div>
          </section>

          {/* Billing address */}
          <section className="mb-7 sm:mb-8">
            <h2 className="text-base font-bold mb-4">Billing address</h2>
            <div className="rounded-lg border border-gray-200 overflow-hidden divide-y divide-gray-200">
              {[{id:"same",label:"Same as shipping address"},{id:"different",label:"Use a different billing address"}].map(opt => (
                <label key={opt.id} onClick={() => setBillingAddress(opt.id)}
                  className={`flex items-center gap-3 px-4 py-3.5 cursor-pointer ${billingAddress===opt.id?"bg-gray-50":"hover:bg-gray-50"}`}>
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${billingAddress===opt.id?"border-[#1A1A1A]":"border-gray-300"}`}>
                    {billingAddress===opt.id && <div className="w-2 h-2 rounded-full bg-[#1A1A1A]"/>}
                  </div>
                  <span className="text-sm font-medium">{opt.label}</span>
                </label>
              ))}
            </div>
          </section>

          {/* Pay button */}
          <button onClick={handlePayNow} disabled={placing}
            className="w-full rounded-xl py-3.5 sm:py-4 text-sm font-bold text-[#1A1A1A] hover:brightness-95 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ background: YELLOW }}>
            {payBtnLabel()}
          </button>

          <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-6 mb-4 text-xs text-gray-400">
            {[
              { label: "Refund policy",     to: "/refund-policy" },
              { label: "Shipping",          to: "/refund-policy" },
              { label: "Privacy policy",    to: "/privacy-policy" },
              { label: "Terms of service",  to: "/terms-of-service" },
              { label: "Cancellations",     to: "/refund-policy" },
              { label: "Contact",           to: "/#corporate-enquiry" },
            ].map(l => (
              <Link key={l.label} to={l.to} className="hover:text-[#1A1A1A] underline underline-offset-2">{l.label}</Link>
            ))}
          </div>
        </div>

        {/* RIGHT — desktop only, sticky */}
        <div className="hidden lg:block">
          <div className="sticky top-0 h-screen overflow-y-auto">
            <OrderSummary
              cartItems={cartItems}
              coupon={coupon} couponInput={couponInput} setCouponInput={setCouponInput}
              couponError={couponError} applyingCoupon={applyingCoupon}
              onApply={handleApplyCoupon} onRemove={handleRemoveCoupon}
            />
          </div>
        </div>
      </div>
    </div>
  );
}